import { Box, Stack } from '@mui/material';
import { sub } from 'date-fns';
import { ReactNode, useMemo, useState } from 'react';

import { SensorAlertTypes } from '@/api/customSensorAlerts/types';
import { useDevice } from '@/api/devices';
import { BarometricPressureAlertReferenceChart } from '@/components/spotter/notificationTab/BarometricPressureAlertReferenceChart';
import { MeanWavePeriodAlertReferenceChart } from '@/components/spotter/notificationTab/MeanWavePeriodAlertReferenceChart';
import { PeakWavePeriodAlertReferenceChart } from '@/components/spotter/notificationTab/PeakWavePeriodAlertReferenceChart';
import { SeaSurfaceTemperatureAlertReferenceChart } from '@/components/spotter/notificationTab/SeaSurfaceTemperatureAlertReferenceChart';
import {
    BroadbandSplReferenceChart,
    CurrentsAlertReferenceChart,
    DisslovedOxygenConcentrationAlertReferenceChart,
    SalinityAlertReferenceChart,
    SubsurfaceTemperatureAlertReferenceChart,
    TurbidityAlertReferenceChart,
    WaterPressureAlertReferenceChart,
} from '@/components/spotter/notificationTab/SensorAlertReferenceChart';
import { SignificantWaveHeightAlertReferenceChart } from '@/components/spotter/notificationTab/SignificantWaveHeightAlertReferenceChart';
import { Timeframe, TimeframeButtonGroup } from '@/components/spotter/notificationTab/TimeframeButtonGroup';
import { WindSpeedAlertReferenceChart } from '@/components/spotter/notificationTab/WindSpeedAlertReferenceChart';
import { FixedDateRangeContextProvider } from '@/context/DateRangeContext';
import { useTzContext } from '@/hooks/useTzContext';

export const NoReferenceChartAvailable = () => <></>;

export type AlertTriggerReferenceChartProps = {
    enabled: boolean;
};

export const alertTriggerTypeToReferenceChartMap: Record<
    SensorAlertTypes,
    (props: AlertTriggerReferenceChartProps) => ReactNode
> = {
    spotter_threshold_seasurfacetemperature: SeaSurfaceTemperatureAlertReferenceChart,
    spotter_threshold_significantwaveheight: SignificantWaveHeightAlertReferenceChart,
    spotter_threshold_peakwaveperiod: PeakWavePeriodAlertReferenceChart,
    spotter_threshold_meanwaveperiod: MeanWavePeriodAlertReferenceChart,
    spotter_threshold_windspeed: WindSpeedAlertReferenceChart,
    spotter_threshold_barometricpressure: BarometricPressureAlertReferenceChart,
    smartmooring_threshold_broadband_spl: BroadbandSplReferenceChart,
    smartmooring_threshold_currents_speed: CurrentsAlertReferenceChart,
    smartmooring_threshold_dissolved_oxygen_concentration: DisslovedOxygenConcentrationAlertReferenceChart,
    smartmooring_threshold_dissolved_oxygen_saturation: NoReferenceChartAvailable,
    smartmooring_threshold_temperature: SubsurfaceTemperatureAlertReferenceChart,
    smartmooring_threshold_salinity: SalinityAlertReferenceChart,
    smartmooring_threshold_turbidity: TurbidityAlertReferenceChart,
    smartmooring_threshold_water_pressure: WaterPressureAlertReferenceChart,
};

export const AlertReferenceChart = ({ alertType, enabled }: { alertType: SensorAlertTypes; enabled: boolean }) => {
    const [timeframe, setTimeframe] = useState<Timeframe>([3, 'days']);
    const tz = useTzContext();
    const { data: device } = useDevice();

    const ReferenceChart = alertTriggerTypeToReferenceChartMap[alertType];

    const availableTimeframes: Timeframe[] = useMemo(
        () => [
            [1, 'days'],
            [3, 'days'],
            [10, 'days'],
            [20, 'days'],
        ],
        []
    );

    const endDate = useMemo(
        () => (device?.timeLastUpdatedUTC ? new Date(device.timeLastUpdatedUTC) : new Date()),
        [device?.timeLastUpdatedUTC]
    );

    const startDate = useMemo(() => {
        const [value, units] = timeframe;
        return sub(endDate, { [units]: value }, tz);
    }, [endDate, timeframe, tz]);

    return (
        <Stack sx={{ my: 2, flexGrow: 1 }} spacing={2}>
            <Box sx={{ ml: 3 }}>
                <TimeframeButtonGroup
                    currentTimeframe={timeframe}
                    availableTimeframes={availableTimeframes}
                    onTimeframeChange={setTimeframe}
                />
            </Box>
            <FixedDateRangeContextProvider startDate={startDate} endDate={endDate}>
                <Box sx={{ height: 300, position: 'relative' }}>
                    {ReferenceChart && <ReferenceChart enabled={enabled} />}
                </Box>
            </FixedDateRangeContextProvider>
        </Stack>
    );
};
